// Global account store for managing player data across the app
import PlayerService from '../../services/PlayerService';
import { setCookie, getCookie, removeCookie } from '../../utils/CookieManager';

class AccountStore {
  constructor() {
    if (AccountStore.instance) {
      return AccountStore.instance;
    }
    AccountStore.instance = this;

    // Public account variables
    this.currentProfile = null;
    this.ranks = {
      0: "Signal Seeker",
      1: "Void Aligned",
      2: "Sentient",
      3: "Frequency Specialist",
      4: "Echo Bound",
      5: "Resonance Manipulator",
      6: "VOID"
    };
    this.rankColors = {
      0: "#A9A9A9", // Light Gray
      1: "#00BFFF", // Deep Sky Blue
      2: "#9370DB", // Medium Purple
      3: "#FFD700", // Gold
      4: "#008080", // Teal
      5: "#8A2BE2", // Blue Violet
      6: "#FD6907"  // Dark Orange
    };
    this.subscribers = new Set();

    // Try to load profile from cookies
    const storedProfile = getCookie('linkedProfile');
    this.currentProfile = storedProfile;
  }

  // Refresh current profile data
  async refreshProfile() {
    if (!this.currentProfile?.playerId) {

      return null;
    }

    try {

      const freshProfile = await PlayerService.getPlayerData(this.currentProfile.playerId);
      if (freshProfile && freshProfile.data) {
        this.setProfile(freshProfile);

        return freshProfile;
      } else {
        console.error('Failed to refresh profile - no data returned');
        return null;
      }
    } catch (error) {
      console.error('Error refreshing profile:', error);
      return null;
    }
  }

  // Update current profile
  setProfile(profile) {
    if (profile?.data) {
      // Check and reset qubit window if expired
      if (profile.data.qubitWindow) {
        const now = Date.now();
        const windowAge = now - profile.data.qubitWindow.startTime;
        
        // If window has expired (24h = 86400000ms), reset it
        if (windowAge >= 86400000) {
          profile.data.qubitWindow = {
            startTime: now,
            totalQubits: 0
          };
          
          // Update the server with reset window
          PlayerService.updatePlayerData(profile.playerId, {
            qubitWindow: profile.data.qubitWindow
          }, { increment: false });
        }
      }
    }
    
    this.currentProfile = profile;
    if (profile) {
      setCookie('linkedProfile', profile);
    } else {
      removeCookie('linkedProfile');
    }
    this.notifySubscribers();
  }

  // Get current profile
  getProfile() {
    return this.currentProfile;
  }

  // Get player's rank
  getRank() {
    const rank = this.currentProfile?.data?.rank || 0;

    return rank;
  }

  // Get player's rank name
  getRankName() {
    const rankName = this.ranks[this.getRank()];

    return rankName;
  }

  // Get player's rank color
  getRankColor() {
    const color = this.rankColors[this.getRank()];

    return color;
  }

  // Get player's qubits
  getQubits() {
    const qubits = this.currentProfile?.data?.qubits || 0;

    return qubits;
  }

  // Logout user and clear all auth state
  logout() {
    console.log('=== AccountStore LOGOUT DEBUG ===');
    console.log('Before logout - localStorage discord_uid:', localStorage.getItem('discord_uid'));
    
    // Clear profile and all auth cookies
    this.currentProfile = null;
    removeCookie('linkedProfile');
    removeCookie('discordUID');
    removeCookie('discord_id');
    
    // Clear shared domain cookie for cross-domain logout
    const domain = window.location.hostname.includes('localhost') 
      ? 'localhost' 
      : window.location.hostname.split('.').slice(-2).join('.');
    
    document.cookie = `discord_uid=; path=/; domain=${window.location.hostname.includes('localhost') ? '' : '.'}${domain}; max-age=0`;
    console.log('Cleared shared domain cookie for:', domain);
    
    // Reset any other state if needed
    
    // Notify subscribers of logout
    this.notifySubscribers();
    
    console.log('AccountStore logout completed');
    return null; // Return null to help with state updates
  }

  // Get number of allowed reality hexes based on rank
  getAllowedHexCount() {
    const rank = this.getRank();
    // Base hexes start at 5, increase by 7 each rank
    // Rank 0: 5 hexes
    // Rank 1: 12 hexes
    // Rank 2: 19 hexes
    // Rank 3: 26 hexes
    // Rank 4: 33 hexes
    // Rank 5: 40 hexes
    // Rank 6: All hexes (50)
    return rank === 6 ? 50 : 5 + (rank * 7);
  }

  // Calculate content probabilities based on rank and qubits
  getContentProbabilities() {
    const rank = this.getRank();
    const qubits = this.getQubits();
    
    // Base probabilities increase with rank
    const baseProbabilities = {
      quantumSignature: 0.1 + (rank * 0.05),
      voidEcho: 0.05 + (rank * 0.03),
      realityFragment: 0.02 + (rank * 0.02),
      timelineData: 0.03 + (rank * 0.04),
      dimensionalRift: 0.01 + (rank * 0.02)
    };

    // Qubit multiplier: more qubits = higher probabilities
    const qubitMultiplier = Math.log10(qubits + 1) * 0.1;

    // Apply multiplier and ensure probabilities don't exceed 1
    return Object.entries(baseProbabilities).reduce((acc, [key, prob]) => {
      acc[key] = Math.min(prob * (1 + qubitMultiplier), 1);
      return acc;
    }, {});
  }

  // Subscribe to store changes
  subscribe(callback) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  // Notify subscribers of changes
  notifySubscribers() {
    this.subscribers.forEach(callback => callback(this.currentProfile));
  }
}

// Create and export singleton instance
const accountStore = new AccountStore();
export default accountStore;